import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType, SelectInput} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import {IMediaDetails} from '../../model/mediaDetails';
import Optional from '../../model/optional';
import videoApi from '../../service/videoApi';
import {IWorkerState} from '../../model/workerState';
import {MinWidthButtonWrapper} from '../util/StyledElements';
import '../../static/css/modal.less';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import {IFolderTreeNode} from '../../model/folderTreeNode';
import {foldersStore} from '../../context/foldersProvider';
import config from '../../service/config';
import folderApi from '../../service/folderApi';
import logging from '../../service/logging';
import {isWellKnownFolder} from '../../constants/wellKnownFolder';

export const CopyMediaModalBase: React.FC<ICopyMediaModalProps & IStateMappedProps> = ({onClose, hideConfirmationButtons, selectedMediaUrl, activeFolder, folderTree}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const rootFolderDropdownValue = 'root';
   const [folderOptions, setFolderOptions] = useState([
      {label: t('details.rootFolderName'), value: rootFolderDropdownValue}
   ]);
   const loggedInUserIsOwner = config.libraryConfig?.loggedInUserIsProfileOwner || config.teamConfig?.loggedInUserHasCreatePermission || config.watchConfig?.isOwner;
   const getFolderTreeForLoggedInUser = async () => {
      const loggedInUserFolderTree = loggedInUserIsOwner ? folderTree : await folderApi.getFolderTree(config.user.TechSmithId);
      const updatedFolderOptions = folderOptions;
      loggedInUserFolderTree?.SubFolders.forEach(f => {
         updatedFolderOptions.push({label: f.Name, value: f.Hash});
         f.SubFolders.forEach(subfolder => {
            updatedFolderOptions.push({label: ` - ${subfolder.Name}`, value: subfolder.Hash});
         });
      });
      setFolderOptions(Object.assign([], updatedFolderOptions));
   };

   useEffect(() => {
      void getFolderTreeForLoggedInUser();
   }, []);

   const [copyState, setCopyState] = useState<ICopyState>({
      didCopyMedia: false,
      isVisible: true,
      isWorking: false,
      hasError: false,
      selectedFolderHash: loggedInUserIsOwner ?
         config.watchConfig?.media.ParentFolder?.Hash ??
         (isWellKnownFolder(activeFolder?.Hash) ? rootFolderDropdownValue : activeFolder?.Hash) ??
         rootFolderDropdownValue :
         rootFolderDropdownValue,
      copiedMedia: null
   });

   const onCopy = async () => {
      let copiedMedia: IMediaDetails;
      setCopyState({
         ...copyState,
         isWorking: true
      });
      try {
         const folderHash: string = copyState.selectedFolderHash !== rootFolderDropdownValue ? copyState.selectedFolderHash : null;
         copiedMedia = await videoApi.copyMedia(selectedMediaUrl, folderHash);

         setCopyState({
            ...copyState,
            didCopyMedia: true,
            isVisible: !shouldCloseAutomaticallyAfterCopyingMedia(),
            isWorking: false,
            copiedMedia: copiedMedia
         });
      } catch (err) {
         logging.error(`Failed to copy media: ${selectedMediaUrl}`, err);
         setCopyState({
            ...copyState,
            didCopyMedia: true,
            isWorking: false,
            hasError: true
         });
      }
   };

   useEffect(() => {
      if (shouldCloseAutomaticallyAfterCopyingMedia()) {
         onCloseCopy();
      }
   }, [copyState.copiedMedia]);

   const shouldCloseAutomaticallyAfterCopyingMedia = (): boolean => hideConfirmationButtons && copyState.didCopyMedia;

   const onHide = () => {
      setCopyState({
         ...copyState,
         isVisible: false
      });
   };

   const onCloseCopy = () => {
      onClose(copyState.copiedMedia);
   };

   const onViewNow = () => {
      onHide();
      if (copyState.copiedMedia) {
         window.location.href = copyState.copiedMedia.Url;
      }
   };

   const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setCopyState({
         ...copyState,
         selectedFolderHash: e.target.value
      });
   };

   const shouldShowCopySuccessConfirmationButtons = (): boolean => copyState.didCopyMedia && !copyState.hasError;

   const shouldShowCopyErrorButtons = (): boolean => copyState.didCopyMedia && copyState.hasError;

   const title = copyState.didCopyMedia
      ? copyState.isWorking ? t('copyMedia.copyingToLibrary') : copyState.hasError ? t('copyMedia.failed') : t('copyMedia.success')
      : t('copyMedia.titleText');

   return (
      <BasicModalPortal
         visible={copyState.isVisible}
         title={title}
         width={cssConstants.defaultModalWidth}
         onClose={copyState.isWorking ? onHide : onCloseCopy}
         themeName={theme}
         isLoading={copyState.isWorking && copyState.isVisible}
         testId="copy-media-modal"
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               <div className="form-field-group">
                  {copyState.didCopyMedia ?
                     !copyState.hasError ? (
                        <span>
                           {t('copyMedia.redirect')}
                        </span>
                     ) : (
                        <span className="error-message">
                           {t('general.errorText')}
                        </span>
                     ) : (
                        <SelectInput
                           onChange={onSelectChange}
                           options={folderOptions}
                           label={t('copyMedia.selectDestination')}
                           themeName={theme}
                           isFullWidth={true}
                           value={copyState.selectedFolderHash}
                        />
                     )}
               </div>
               <div className="button-group">
                  <MinWidthButtonWrapper>
                     {!copyState.didCopyMedia && (
                        <>
                           <ControlButton
                              label={t('general.cancel')}
                              buttonType={TscButtonType.secondary}
                              disabled={false}
                              themeName={theme}
                              onClick={onCloseCopy}
                              testId="cancel"
                           />
                           <ControlButton
                              label={t('general.copy')}
                              buttonType={TscButtonType.primary}
                              disabled={false}
                              themeName={theme}
                              onClick={onCopy}
                              testId="copy"
                           />
                        </>
                     )}
                     {shouldShowCopySuccessConfirmationButtons() && (
                        <>
                           <ControlButton
                              label={t('copyMedia.noThanks')}
                              buttonType={TscButtonType.secondary}
                              disabled={false}
                              themeName={theme}
                              onClick={onCloseCopy}
                              testId="no-thanks"
                           />
                           <ControlButton
                              label={t('copyMedia.viewNow')}
                              buttonType={TscButtonType.primary}
                              disabled={false}
                              themeName={theme}
                              onClick={onViewNow}
                              testId="view-now"
                           />
                        </>
                     )}
                     {shouldShowCopyErrorButtons() && (
                        <ControlButton
                           label={t('general.close')}
                           buttonType={TscButtonType.primary}
                           disabled={false}
                           themeName={theme}
                           onClick={onCloseCopy}
                           testId="error-close"
                        />
                     )}
                  </MinWidthButtonWrapper>
               </div>
            </div>
         </div>
      </BasicModalPortal>
   );
};

export interface ICopyMediaModalProps {
   onClose(copiedMedia?: IMediaDetails): void;
   hideConfirmationButtons?: boolean;
   selectedMediaUrl: string;
}

export interface IStateMappedProps {
   activeFolder: IFolderTreeNode;
   folderTree: IFolderTreeNode;
}

interface ICopyState extends IWorkerState {
   isVisible: boolean;
   didCopyMedia: boolean;
   selectedFolderHash: Optional<string>;
   copiedMedia: Optional<IMediaDetails>;
}

const mapStatesToProps = (
   {hideConfirmationButtons, selectedMediaUrl, onClose}: ICopyMediaModalProps,
   {activeFolder, folderTree}: Partial<IStateMappedProps>
): ICopyMediaModalProps & IStateMappedProps => ({hideConfirmationButtons, selectedMediaUrl, onClose, activeFolder, folderTree});

export default withMemoizedContexts(mapStatesToProps, foldersStore)(CopyMediaModalBase);
